import type { PostHog } from "posthog-js";
import { getOrCreateExternalId, readAttribution } from "@/lib/attribution";
import { exceptionSignature, isThirdPartyNoise } from "@/lib/errorNoise";
import { isCardJob } from "@/lib/cards";
import { isFamilyCardStyle, isPetStyle, readFunnelStyle } from "@/lib/funnelSteps";
import { SAVE_DESIGN_SAVED_EVENT } from "@/lib/saveDesignExperiment";
import { marketForLocale } from "@/lib/market";

export const POSTHOG_DISTINCT_ID_HEADER = "X-PostHog-Distinct-Id";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY || "phc_pzq8F7ZRDkDzJGThmKviE6AHEBpACJz6cje2WefzJvnm";
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://tunnel.vellumprintworks.com";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let initialized = false;
// The SDK, once its module has loaded and init() has run. posthog-js is a dynamic import: it is
// ~68 KB (brotli) of script, and as a static import every page downloaded and parsed it before the
// first paint (on the landers, inside the LCP window) only to sit unused until DeferredThirdParty
// called initPostHog() at the first interaction or 6 s after load.
let client: PostHog | null = null;
const readyQueue: Array<(posthog: PostHog) => void> = [];

/**
 * Runs `fn` now if PostHog is initialised, otherwise queues it until init.
 * Init is deferred to first interaction / post-load idle (see DeferredThirdParty)
 * to keep posthog-js out of the critical rendering window, so mount-time flag
 * reads and captures must go through this to avoid being dropped.
 */
export function onPostHogReady(fn: (posthog: PostHog) => void) {
  if (client) fn(client);
  else readyQueue.push(fn);
}

export function initPostHog() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  void import("posthog-js").then(({ default: posthog }) => {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only",
      capture_pageview: false,
      capture_pageleave: false,
      // Seed only a NEW SDK identity. Anonymous bootstrap.distinctID overwrites
      // persisted IDs without merging, splitting a returning visitor into people.
      get_device_id: (generatedId) => getOrCreateExternalId() ?? generatedId,
      // SSR evaluates flags using this cookie. Prefer it over stale per-origin
      // localStorage when both exist (option name supported by our locked SDK).
      __preview_cookie_wins_on_conflict: true,
      // Exception autocapture listens on window.onerror, which hears every script
      // on the page, ours or not. Drop the third-party in-app-browser and
      // extension noise here so the error list stays a list of our own bugs.
      before_send: (event) => {
        if (!event || event.event !== "$exception") return event;
        return isThirdPartyNoise(exceptionSignature(event.properties)) ? null : event;
      },
    });
    client = posthog;
    for (const fn of readyQueue.splice(0)) fn(posthog);
  });
}

function isBrowser() {
  return typeof window !== "undefined";
}

function cleanProperties(properties: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  );
}

function currentPageProperties() {
  if (!isBrowser()) return {};
  return {
    page_path: window.location.pathname,
    page_search: window.location.search || undefined,
    page_url: window.location.href,
    referrer: document.referrer || undefined,
  };
}

export function normalizeEmail(email: string) {
  const normalized = email.trim().toLowerCase();
  return EMAIL_RE.test(normalized) ? normalized : null;
}

export function emailDomain(email: string) {
  return normalizeEmail(email)?.split("@")[1];
}

export function getPostHogDistinctId() {
  if (!client) return undefined;
  try {
    return client.get_distinct_id();
  } catch {
    // get_distinct_id() throws if PostHog hasn't initialised yet.
    return undefined;
  }
}

export function getPostHogHeaders(): Record<string, string> {
  const distinctId = getPostHogDistinctId();
  return distinctId ? { [POSTHOG_DISTINCT_ID_HEADER]: distinctId } : {};
}

// /uk is the en-GB locale; every other prefix is its own locale code.
function detectLocale(): string {
  if (!isBrowser()) return "nl";
  const match = window.location.pathname.match(/^\/(nl|de|en|uk)\b/);
  return match?.[1] === "uk" ? "en-GB" : match?.[1] ?? "nl";
}

// Pets are their own product line, so every event says which one it belongs to: `funnel` is
// "pet_art_order" on the pets line and "car_art_order" for cars and motorcycles, and a car funnel or
// experiment never counts a pet shopper. The pet pages are pets by their path. The pages pets share
// with cars (orientation, processing, preview, checkout) carry the job id, and the job's style is
// remembered by the funnel (rememberFunnelStyle) from the pet upload on.
const PET_PATH = /^(?:\/(?:nl|de|en|uk))?\/(?:pets|huisdieren|haustiere)(?:\/|$)/;
const JOB_ID_IN_PATH = /\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i;

export function isPetJob(jobId: string | null | undefined): boolean {
  return isBrowser() && !!jobId && isPetStyle(readFunnelStyle(jobId));
}

/** Whether the page being looked at is on the pets line. */
export function isPetFunnel(): boolean {
  if (!isBrowser()) return false;
  const path = window.location.pathname;
  return PET_PATH.test(path) || isPetJob(path.match(JOB_ID_IN_PATH)?.[1]);
}

// Christmas cards are a third line: the cards lander and the family card's own pages (by their public
// path or their internal /card-family* one), any page of a job made for cards — a family card, known
// by the style the server returned for it (rememberFunnelStyle, which the preview and checkout pages
// write from their API response on any device), or a job started from the cards lander (lib/cards
// rememberCardJob) — and a visitor who came in on a cards angle, whose every upload the server stamps
// with that angle and books on the cards line (backend ChristmasCards.IsCardJob). A card shopper is
// never counted in a car or pet funnel or experiment.
const CARD_PATH = /^(?:\/en)?\/(?:christmas-cards|card-family)(?:[\/-]|$)/;

/** Whether this job was made for Christmas cards, as far as this browser knows. */
export function isCardJobId(jobId: string | null | undefined): boolean {
  return isBrowser() && !!jobId && (isCardJob(jobId) || isFamilyCardStyle(readFunnelStyle(jobId)));
}

/** Whether the page being looked at is on the Christmas cards line. */
export function isCardFunnel(): boolean {
  if (!isBrowser()) return false;
  const path = window.location.pathname;
  return (
    CARD_PATH.test(path) ||
    isCardJobId(path.match(JOB_ID_IN_PATH)?.[1]) ||
    !!readAttribution().acquisitionAngle?.toLowerCase().startsWith("cards")
  );
}

function sharedProperties() {
  return {
    app: "vellum_frontend",
    funnel: isCardFunnel() ? "card_order" : isPetFunnel() ? "pet_art_order" : "car_art_order",
    event_version: 1,
    locale: detectLocale(),
    market: marketForLocale(detectLocale()),
  };
}

export function registerSharedPostHogProperties() {
  if (!isBrowser()) return;
  const shared = sharedProperties();
  onPostHogReady((posthog) => posthog.register(shared));
}

export function capturePostHogEvent(event: string, properties: Record<string, unknown> = {}) {
  if (!isBrowser()) return;
  // Snapshot page/shared properties at call time: the queued capture may run
  // after a route change, and the event must describe the page it happened on.
  const shared = sharedProperties();
  const props = cleanProperties({
    ...shared,
    ...currentPageProperties(),
    ...properties,
  });
  onPostHogReady((posthog) => {
    posthog.register(shared);
    posthog.capture(event, props);
  });
}

/**
 * Feeds PostHog's own experiment dashboard one `$feature_flag_called` for a flag the server already
 * resolved. Exactly once: the flags may not be loaded yet when the page mounts, so this waits for
 * them, and then unsubscribes. A listener that stays registered fires again on every later flag
 * reload for the life of the tab and reports whatever the flag says *then* — that is how the
 * theme-picker exposure was re-reported from the checkout page under a different variant.
 */
export function reportFlagExposure(flagKey: string) {
  if (!isBrowser()) return;
  onPostHogReady((posthog) => {
    // posthog-js runs the callback synchronously when the flags are already loaded — the normal
    // case, since they load on the landing page — so the unsubscribe handle does not exist yet
    // inside the callback. Report first, then unsubscribe once the handle is in hand.
    let done = false;
    let unsubscribe: (() => void) | undefined;
    unsubscribe = posthog.onFeatureFlags(() => {
      if (done) return;
      done = true;
      posthog.getFeatureFlag(flagKey);
      unsubscribe?.();
    });
    if (done) unsubscribe();
  });
}

export function trackPageView(pathname: string, search: string) {
  const searchWithPrefix = search ? `?${search}` : "";
  capturePostHogEvent("$pageview", {
    page_path: pathname,
    page_search: searchWithPrefix || undefined,
    page_url: isBrowser() ? `${window.location.origin}${pathname}${searchWithPrefix}` : undefined,
  });
}

/**
 * Mirror cookie-persisted attribution into PostHog super-properties so every
 * subsequent event auto-includes acquisition_angle / utm_* — used by the
 * funnel-by-angle analytics query.
 */
export function registerAttributionForPostHog(attribution: {
  acquisitionAngle?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}) {
  if (!isBrowser()) return;
  const props = cleanProperties({
    acquisition_angle: attribution.acquisitionAngle,
    utm_source: attribution.utmSource,
    utm_medium: attribution.utmMedium,
    utm_campaign: attribution.utmCampaign,
  });
  if (Object.keys(props).length === 0) return;
  onPostHogReady((posthog) => posthog.register(props));
}

/**
 * Ties this browser to the person behind an email without touching the distinct id.
 *
 * `posthog.identify(email)` used to swap the distinct id for the email, and posthog-js answers a
 * swap by re-fetching every flag for the new id — which hashes differently, so half the visitors
 * landed in the other arm of every running experiment the moment they typed their email. PostHog
 * then saw two variants for one person and dropped them from the results (the theme-picker test
 * lost 7 of its 9 grouped purchases that way).
 *
 * `$create_alias` does the same merge on the server — this browser's anonymous person becomes a
 * distinct id of the email's person, so the same customer on a phone and a laptop is one person —
 * but the browser keeps its own id, so no flag is re-fetched and no arm flips. The server-side
 * purchase event uses the job's stored distinct id and lands on that same person.
 *
 * Aliased once per browser: posthog-js remembers the alias in `__alias`, and PostHog will not merge
 * an already-identified person into another one anyway, so a second email in the same browser stays
 * a separate person — the same rule identify() had.
 */
function recordEmail(email: string, personProperties: Record<string, unknown>) {
  onPostHogReady((posthog) => {
    const distinctId = posthog.get_distinct_id();
    if (distinctId && posthog.get_property("__alias") !== distinctId) {
      posthog.alias(distinctId, email);
    }
    posthog.setPersonProperties({ email, ...cleanProperties(personProperties) });
  });
}

/**
 * The account equivalent of {@link recordCheckoutEmail}: records who this browser's visitor is and
 * merges this browser into the account holder's person, so their phone and laptop are one person.
 */
export function recordAccountEmail(email: string, properties: Record<string, unknown> = {}) {
  const normalized = normalizeEmail(email);
  if (!normalized || !isBrowser()) return false;

  recordEmail(normalized, { has_account: true, ...properties });
  capturePostHogEvent("account_identified", {
    email_domain: emailDomain(normalized),
    ...properties,
  });

  return true;
}

export function recordCheckoutEmail(email: string, properties: Record<string, unknown> = {}) {
  const normalized = normalizeEmail(email);
  if (!normalized || !isBrowser()) return false;

  recordEmail(normalized, {});
  capturePostHogEvent("checkout_email_captured", {
    email_domain: emailDomain(normalized),
    ...properties,
  });

  return true;
}

/**
 * The save-design card's equivalent of {@link recordCheckoutEmail}: ties this browser to the person
 * behind the address and reports the save. The event carries the domain, never the address.
 */
export function recordSavedDesignEmail(email: string, properties: Record<string, unknown> = {}) {
  const normalized = normalizeEmail(email);
  if (!normalized || !isBrowser()) return false;

  recordEmail(normalized, {});
  capturePostHogEvent(SAVE_DESIGN_SAVED_EVENT, {
    email_domain: emailDomain(normalized),
    ...properties,
  });

  return true;
}

/**
 * Reports a crash caught by a React error boundary.
 *
 * Initialises PostHog first: init is normally deferred to the visitor's first
 * interaction, and a page that breaks on its first render gives them nothing to
 * interact with — so without this, the crashes that cost the most are the ones
 * we never hear about.
 */
export function captureAppCrash(error: Error & { digest?: string }, boundary: string) {
  if (!isBrowser()) return;
  initPostHog();
  const props = cleanProperties({
    ...sharedProperties(),
    ...currentPageProperties(),
    boundary,
    error_digest: error.digest,
  });
  onPostHogReady((posthog) => {
    posthog.captureException(error, props);
    posthog.capture("app_crash", props);
  });
}
